/**
 * Tour Indicator Component
 *
 * A contextual help button that appears on pages to allow users to restart
 * feature tours. This component provides a consistent, non-intrusive way for
 * users to discover and access tours throughout the application.
 *
 * Features:
 * - Tooltip with tour description
 * - Consistent positioning and styling
 * - Optional badge to show "New" or completion status
 * - Accessible with keyboard navigation
 *
 * @example
 * <TourIndicator
 *   tourName="Dashboard Tour"
 *   description="Learn about your dashboard features"
 *   onRestart={() => restartDashboardTour()}
 *   position="top-right"
 * />
 */

"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HelpCircle, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export interface TourIndicatorProps {
  /** Name of the tour (displayed in tooltip) */
  tourName: string;
  /** Brief description of what the tour covers */
  description: string;
  /** Callback to restart the tour */
  onRestart: () => void;
  /** Position of the indicator on the page */
  position?: "top-right" | "top-left" | "bottom-right" | "bottom-left" | "inline";
  /** Optional badge text (e.g., "New", "Updated") */
  badge?: string;
  /** Icon to display (default: HelpCircle) */
  icon?: "help" | "sparkles";
  /** Custom className for additional styling */
  className?: string;
  /** Whether to show as a floating button or inline */
  variant?: "floating" | "inline";
}

export function TourIndicator({
  tourName,
  description,
  onRestart,
  position = "top-right",
  badge,
  icon = "help",
  className = "",
  variant = "floating",
}: TourIndicatorProps) {
  const IconComponent = icon === "sparkles" ? Sparkles : HelpCircle;

  // Position classes for floating variant
  const positionClasses = {
    "top-right": "fixed top-20 right-4 z-40",
    "top-left": "fixed top-20 left-4 z-40",
    "bottom-right": "fixed bottom-20 right-4 z-40",
    "bottom-left": "fixed bottom-20 left-4 z-40",
    inline: "",
  };

  const buttonClass = variant === "floating"
    ? `${positionClasses[position]} shadow-lg ${className}`
    : `${className}`;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="relative inline-block">
            <Button
              onClick={onRestart}
              variant={variant === "floating" ? "default" : "outline"}
              size={variant === "floating" ? "icon" : "sm"}
              className={buttonClass}
              aria-label={`Restart ${tourName}`}
            >
              <IconComponent className={variant === "floating" ? "h-5 w-5" : "h-4 w-4 mr-2"} />
              {variant === "inline" && <span className="text-sm">Tour</span>}
            </Button>
            {badge && (
              <Badge
                variant="secondary"
                className="absolute -top-2 -right-2 px-1.5 py-0.5 text-xs"
              >
                {badge}
              </Badge>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side={variant === "floating" ? "left" : "top"} className="max-w-xs">
          <div className="space-y-1">
            <p className="font-semibold">{tourName}</p>
            <p className="text-sm text-muted-foreground">{description}</p>
            <p className="text-xs text-muted-foreground mt-2">Click to restart tour</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Compact Tour Button - A smaller inline variant
 *
 * @example
 * <CompactTourButton
 *   label="Dashboard Tour"
 *   onRestart={() => restartDashboardTour()}
 * />
 */
export interface CompactTourButtonProps {
  label: string;
  onRestart: () => void;
  className?: string;
}

export function CompactTourButton({
  label,
  onRestart,
  className = "",
}: CompactTourButtonProps) {
  return (
    <Button
      onClick={onRestart}
      variant="ghost"
      size="sm"
      className={`text-xs text-muted-foreground hover:text-primary ${className}`}
      aria-label={`Restart ${label}`}
    >
      <HelpCircle className="h-3 w-3 mr-1.5" />
      {label}
    </Button>
  );
}

/**
 * Page Tour Header - A header section with tour info
 *
 * @example
 * <PageTourHeader
 *   title="Dashboard Features"
 *   description="New to this page? Take a quick tour to learn the features."
 *   onTakeTour={() => restartDashboardTour()}
 * />
 */
export interface PageTourHeaderProps {
  title: string;
  description: string;
  onTakeTour: () => void;
  onDismiss?: () => void;
  show?: boolean;
}

export function PageTourHeader({
  title,
  description,
  onTakeTour,
  onDismiss,
  show = true,
}: PageTourHeaderProps) {
  if (!show) return null;

  return (
    <div className="bg-primary/5 border border-primary/10 rounded-lg p-4 mb-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className="p-2 bg-primary/10 rounded-full">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-base mb-1">{title}</h3>
            <p className="text-sm text-muted-foreground mb-3">{description}</p>
            <div className="flex gap-2">
              <Button onClick={onTakeTour} size="sm" variant="default">
                <Sparkles className="h-4 w-4 mr-2" />
                Take Tour
              </Button>
              {onDismiss && (
                <Button onClick={onDismiss} size="sm" variant="ghost">
                  Dismiss
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
